import './index.css';

import React, { useState, useEffect } from 'react';
import { Provider } from 'react-redux';
import { ThemeProvider, createGlobalStyle } from 'styled-components';
import ReactGA from 'react-ga';
import { ApolloProvider } from '@apollo/react-hooks';
import * as Sentry from '@sentry/react';
import { Integrations } from '@sentry/tracing';

import apolloClient from 'apollo';
import 'apollo/middleware';

import store from 'store';
import { sentry } from 'config';

import LoadingPage from 'components/LoadingPage';
import { appSetup, setSyncInterval } from 'actions/gallery';

import { load } from 'i18n';

import Routes from 'Routes';

import theme from 'themes';

ReactGA.initialize('UA-136952110-2');

Sentry.init({
  dsn: sentry.dsn,
  integrations: [new Integrations.BrowserTracing()],
  // Set tracesSampleRate to 1.0 to capture 100%
  // of transactions for performance monitoring.
  // We recommend adjusting this value in production
  tracesSampleRate: sentry.tracesSampleRate,
  environment: process.env.NODE_ENV,
  sampleRate: sentry.sampleRate,
  enabled: false
});

// eslint-disable-next-line
const GlobalStyle = createGlobalStyle`
  ${theme.global}
`;

function MainApp() {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    // Setup
    (async () => {
      await load();
      await store.dispatch(appSetup());

      if (typeof document !== 'undefined' && document.fonts) {
        try {
          await Promise.race([
            Promise.all([
              document.fonts.load('24px "Google Symbols"'),
              document.fonts.ready
            ]),
            new Promise((resolve) => setTimeout(resolve, 2000))
          ]);
        } catch (e) {
          // Fallback gracefully if font loading fails or times out
        }
      }

      setReady(true);
    })();

    // Cleanup
    return () => {
      store.dispatch(setSyncInterval());
    };
  }, [setReady]);

  return (
    <Provider store={store}>
      <ApolloProvider client={apolloClient}>
        <GlobalStyle />
        <ThemeProvider theme={theme}>
          {ready ? <Routes /> : <LoadingPage />}
        </ThemeProvider>
      </ApolloProvider>
    </Provider>
  );
}

export default MainApp;
